import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import './Pom.css';

// Custom hook for managing tab underline positioning
const useTabUnderline = (activeTab, dependencies = []) => {
  const tabRef = useRef(null);
  const [underlineStyle, setUnderlineStyle] = useState({});

  const calculatePosition = useCallback(() => {
    if (!tabRef.current) return null;

    const activeElement = tabRef.current.querySelector('.active');
    if (!activeElement || activeElement.offsetWidth === 0 || activeElement.offsetHeight === 0) {
      return { width: 0, display: 'none' };
    }

    const tabRect = activeElement.getBoundingClientRect();
    const parentRect = tabRef.current.getBoundingClientRect();

    if (tabRect.width > 0 && parentRect.width > 0) {
      return {
        width: tabRect.width,
        left: tabRect.left - parentRect.left,
      };
    }

    return null;
  }, []);

  const updatePosition = useCallback(
    (useRAF = true) => {
      const updateFn = () => {
        const position = calculatePosition();
        if (position) {
          setUnderlineStyle(position);
        }
      };

      if (useRAF) {
        requestAnimationFrame(updateFn);
      } else {
        updateFn();
      }
    },
    [calculatePosition],
  );

  useEffect(() => {
    // Initial positioning with multiple attempts
    const timers = [50, 100, 200, 500].map((delay) => setTimeout(() => updatePosition(), delay));

    return () => timers.forEach(clearTimeout);
  }, [updatePosition]);

  useEffect(() => {
    // Update on active tab change
    const timer = setTimeout(() => updatePosition(), 100);

    // Handle window resize
    const debouncedUpdate = debounce(() => updatePosition(), 300);
    const initialResizeTimer = setTimeout(() => updatePosition(false), 150);

    window.addEventListener('resize', debouncedUpdate);

    return () => {
      clearTimeout(timer);
      clearTimeout(initialResizeTimer);
      window.removeEventListener('resize', debouncedUpdate);
      debouncedUpdate.cancel();
    };
  }, [activeTab, updatePosition, dependencies]);

  return { tabRef, underlineStyle };
};

function PomTabs() {
  const history = useHistory();
  const { t } = useTranslation('common');

  // Create MenuList structure similar to ProductCostingTabMenu
  const MenuList = useMemo(
    () => [
      {
        eventKey: 'point-of-measures',
        eventName: 'POMS.POM_JS.POM_LIBRARY',
        url: '/libraries/point-of-measures',
      },
      {
        eventKey: 'pom-groups',
        eventName: 'POMS.POM_JS.POM_GROUP',
        url: '/libraries/pom-groups',
      },
      {
        eventKey: 'gradings',
        eventName: 'POMS.POM_JS.GRADING_GROUP',
        url: '/libraries/gradings',
      },
    ],
    [],
  );

  // Get current active tab based on URL
  const getCurrentActiveTab = useCallback(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes('point-of-measures')) return 'point-of-measures';
    if (currentPath.includes('pom-groups')) return 'pom-groups';
    if (currentPath.includes('gradings')) return 'gradings';
    return 'point-of-measures'; // default
  }, []);

  const [masterActiveTab, setMasterActiveTab] = useState(getCurrentActiveTab);

  const handleOpenUrl = useCallback(
    (url, eventKey) => {
      setMasterActiveTab(eventKey);
      history.push(url);
    },
    [history],
  );

  // Use the custom hook for underline management
  const { tabRef: switchTabRef, underlineStyle: activeUnderline } = useTabUnderline(masterActiveTab, [MenuList]);

  // Initialize active tab on mount
  useEffect(() => {
    setMasterActiveTab(getCurrentActiveTab());
  }, [getCurrentActiveTab]);

  return (
    <div className="pom-page">
      <div className="d-flex mt-24 mb-24 ml-24">
        <div className="common-switch-container">
          <ul className="new-switch" ref={switchTabRef}>
            {MenuList.map((element) => (
              <li key={element.eventKey} className={masterActiveTab === element.eventKey ? 'active' : ''}>
                <button type="button" className="switch-item" onClick={() => handleOpenUrl(element.url, element.eventKey)}>
                  {t(element.eventName)}
                </button>
              </li>
            ))}
          </ul>
          <div className="tab-underline" style={activeUnderline} />
        </div>
      </div>
    </div>
  );
}

export default PomTabs;
